import { useTranslation } from 'react-i18next';
import { CardPocket } from '@/common/components/CardPocket/CardPocket';
import { Icon } from '@/common/components/Icons/Icon';
import { Translate } from '@/common/components/Translate/Translate';
import { ROUTES } from '@/common/constants/routes';
import { useEmployeeDetails } from '@/common/contexts/user/UserDetailsContext';
import { TCompensationSummary } from '@/common/integrations/menhir/employee/compensation/compensation.types';
import { formatDate, safeParseDate } from '@/common/utils/date';
import { MoneyUtils } from '@coverflex-tech/hypernova-utils';
import { addMonths } from 'date-fns';
import { useRouter } from 'expo-router';

export type TESBenefitsCardPocketProps = {
  summary?: TCompensationSummary;
};

export function ESBenefitsCardPocket({ summary }: TESBenefitsCardPocketProps) {
  const { t } = useTranslation();
  const { employee } = useEmployeeDetails();
  const router = useRouter();

  if (!summary || summary.status === 'created') {
    return (
      <CardPocket
        title={t('dashboard.home.es.wallet.benefits_widget.inactive.title')}
        icon={<Icon icon="seedling" />}
        values={[
          {
            label: (
              <Translate
                i18nKey="dashboard.home.es.wallet.benefits_widget.inactive.description"
                values={{
                  date: formatDate(summary?.renewalDate),
                }}
              />
            ),
          },
        ]}
      />
    );
  }

  // TODO: Uncomment this once the 'blocked' status is implemented
  // if (summary.status === 'blocked') {
  //   return (
  //     <CardPocket
  //       title={t('dashboard.home.es.wallet.benefits_widget.blocked.title')}
  //       icon={<Icon icon="lock" />}
  //       iconBadgeColor="neutral30"
  //       values={[
  //         {
  //           label: (
  //             <Translate i18nKey="dashboard.home.es.wallet.benefits_widget.blocked.description" />
  //           ),
  //         },
  //       ]}
  //       onPress={() => router.push(ROUTES.BENEFITS)}
  //     />
  //   );
  // }

  if (summary.status === 'inactive') {
    const leavingDate = (() => {
      const dt = safeParseDate(employee.leavingAt);
      return dt ? formatDate(addMonths(dt, 6)) : undefined;
    })();
    return (
      <CardPocket
        title={t(
          'dashboard.home.es.wallet.gross_salary_no_longer_available.card.title'
        )}
        values={[
          {
            label: (
              <Translate
                i18nKey="dashboard.home.es.wallet.gross_salary_no_longer_available.card.subtitle"
                values={{
                  date: leavingDate,
                }}
              />
            ),
          },
        ]}
        icon={<Icon icon="seedling" />}
        iconBadgeColor="neutral10"
      />
    );
  }

  return (
    <CardPocket
      title={t('dashboard.home.es.wallet.benefits_widget.active.title')}
      icon={<Icon icon="seedling" />}
      values={summary?.attributions.map((attribution) => ({
        label: t(
          `general.balances.description.${attribution.slug}`
        ),
        value: MoneyUtils.moneyToString(attribution.balance),
      }))}
      onPress={() => router.push(ROUTES.BENEFITS)}
    />
  );
}
